import {ChecklistTemplateEntry} from "../models/checklist-template-entry/checklist-template-entry.model";
import {
  WorkflowOverviewEntry, WorkflowOverviewGroup,
  WorkflowOverviewItem, WorkflowOverviewItemState,
  WorkflowOverviewSeparator,
  WorkflowOverviewTitle
} from "../models/workflow-overview/workflow-overview.model";
import {ChecklistEntryType} from "../enums/checklist-entry/checklist-entry-type.enum";
import {IconProp} from "@fortawesome/fontawesome-svg-core";

export class ChecklistTemplateWorkflowOverviewBuilder {

  public static buildTreeForWorkflowOverview(
    checklistTemplateEntryIds: string[],
    checklistTemplateEntries: Map<string, ChecklistTemplateEntry>,
    disabledChecklistTemplateEntries: Map<string, {label: string, icon: IconProp}>,
    states: Map<string, WorkflowOverviewItemState> = null,
    setInfoText: boolean = false,
    groupItems: boolean = false
  ): WorkflowOverviewItem[] {
    const checklistTemplateEntriesForOverview: ChecklistTemplateEntry[] = checklistTemplateEntryIds
      .map((id: string) => checklistTemplateEntries.get(id))
      .filter((entry: ChecklistTemplateEntry) =>
        entry.type !== ChecklistEntryType.IMAGE && entry.type !== ChecklistEntryType.TABLE_ROW);

    // Initialize items
    const checkedEntryIds: string[] = [];
    const items: Map<string, WorkflowOverviewItem> = new Map<string, WorkflowOverviewItem>();
    const topLevelItems: WorkflowOverviewItem[] = [];

    // Loop through each item in the flat list
    for (const entry of checklistTemplateEntriesForOverview) {
      const checklistTemplateEntryId: string = entry.id;
      const numberingLevel: number = entry.numberingLevel;

      // Create item
      let item: WorkflowOverviewItem;

      switch (entry.type) {
        case ChecklistEntryType.PAGE_BREAK: {
          item = new WorkflowOverviewSeparator();
          break;
        }
        case ChecklistEntryType.TITLE: {
          item = new WorkflowOverviewTitle();
          (item as WorkflowOverviewTitle).numbering = entry.showNumbering ?
            entry.numberingValue : null;
          (item as WorkflowOverviewTitle).name = {...entry.name};
          break;
        }
        case ChecklistEntryType.IMAGE:
        case ChecklistEntryType.TABLE_ROW: {
          continue;
        }
        default: {
          item = new WorkflowOverviewEntry();
          (item as WorkflowOverviewEntry).name = entry.name;
          (item as WorkflowOverviewEntry).numbering = entry.showNumbering ?
            entry.numberingValue : null;
          (item as WorkflowOverviewEntry).state = states?.get(entry.id) || WorkflowOverviewItemState.NOT_FILLED;

          if (setInfoText) {
            if (entry.type === ChecklistEntryType.TABLE) {
              (item as WorkflowOverviewEntry).infoText = this.getConditionReferencesInfoText(
                entry.id,
                checklistTemplateEntryIds,
                checklistTemplateEntries
              );
            } else if (entry.conditionReference) {
              (item as WorkflowOverviewEntry).infoText = entry.conditionReference.toString();
            }
          }
        }
      }

      // Set id
      item.id = checklistTemplateEntryId;

      // Set disabled state
      if (item instanceof WorkflowOverviewEntry && disabledChecklistTemplateEntries?.has(entry.id)) {
        item.disabled = true;
        item.disabledLabel = disabledChecklistTemplateEntries.get(entry.id)?.label;
        item.disabledIcon = disabledChecklistTemplateEntries.get(entry.id)?.icon;
      }

      // Push to collections
      checkedEntryIds.push(checklistTemplateEntryId);
      items.set(checklistTemplateEntryId, item);

      if (numberingLevel === 1) {
        topLevelItems.push(item);
      } else {

        // Find parent by iterating existing items in reverse order (using checkedEntryIds)
        let parentId: string | null = null;
        for (let i = checkedEntryIds.length - 1; i >= 0; i--) {
          const potentialParentId = checkedEntryIds[i];
          const potentialParent: ChecklistTemplateEntry = checklistTemplateEntries.get(potentialParentId);
          if (potentialParent
            && potentialParent.id !== checklistTemplateEntryId
            && potentialParent.numberingLevel === numberingLevel - 1
          ) {
            parentId = potentialParentId;
            break; // Stop iterating after finding a parent
          }
        }

        // If a parent exists, add this item as a child
        if (parentId) {
          const parent: WorkflowOverviewItem = items.get(parentId);
          if (parent && (parent instanceof WorkflowOverviewTitle || parent instanceof WorkflowOverviewEntry)) {
            parent.children.push(item);

            // Set collapsed info text
            if (setInfoText && !parent.collapsedInfoText?.length) {
              parent.collapsedInfoText = this.getConditionReferencesInfoText(
                parent.id,
                checklistTemplateEntryIds,
                checklistTemplateEntries
              );
            }
          }
        }
      }
    }

    if (groupItems) {
      return this.groupItems(topLevelItems, checklistTemplateEntries);
    } else {
      return topLevelItems;
    }
  }

  private static getConditionReferencesInfoText(
    checklistTemplateEntryId: string,
    checklistTemplateEntryIds: string[],
    checklistTemplateEntries: Map<string, ChecklistTemplateEntry>
  ): string {
    if (!checklistTemplateEntryId) {
      return;
    }

    const entry: ChecklistTemplateEntry = checklistTemplateEntries.get(checklistTemplateEntryId);

    let lowestConditionReference: number = entry.conditionReference;
    let highestConditionReference: number = entry.conditionReference;

    let entryIndex: number
      = checklistTemplateEntryIds.findIndex((id: string) => checklistTemplateEntryId === id);

    if (entryIndex === -1 || entryIndex === checklistTemplateEntryIds.length - 1) {
      return;
    }

    entryIndex++;
    let nextEntry: ChecklistTemplateEntry = checklistTemplateEntries.get(
      checklistTemplateEntryIds[entryIndex]
    );

    while (nextEntry != null && nextEntry.numberingLevel > entry.numberingLevel) {
      if (nextEntry.conditionReference != null) {
        if (lowestConditionReference == null || lowestConditionReference > nextEntry.conditionReference) {
          lowestConditionReference = nextEntry.conditionReference;
        }
        if (highestConditionReference == null || highestConditionReference < nextEntry.conditionReference) {
          highestConditionReference = nextEntry.conditionReference;
        }
      }

      if (entryIndex === checklistTemplateEntryIds.length - 1) {
        break;
      }

      entryIndex++;
      nextEntry = checklistTemplateEntries.get(
        checklistTemplateEntryIds[entryIndex]
      );
    }

    if (lowestConditionReference && highestConditionReference) {
      if (lowestConditionReference === highestConditionReference) {
        return (lowestConditionReference || highestConditionReference).toString();
      } else {
        return (lowestConditionReference + '-' + highestConditionReference);
      }
    }
  }

  private static groupItems(
    items: WorkflowOverviewItem[],
    checklistTemplateEntries: Map<string, ChecklistTemplateEntry>
  ): WorkflowOverviewItem[] {
    const groupedItems: WorkflowOverviewItem[] = [];
    let currentGroup: WorkflowOverviewGroup;

    for (const [index, item] of items.entries()) {
      const entry: ChecklistTemplateEntry = checklistTemplateEntries.get(item.id);

      if ((item instanceof WorkflowOverviewTitle || item instanceof WorkflowOverviewEntry) && item.children?.length) {
        item.children = ChecklistTemplateWorkflowOverviewBuilder.groupItems(item.children, checklistTemplateEntries);
      }

      let nextItem: WorkflowOverviewItem = null;
      let nextEntry: ChecklistTemplateEntry = null;

      if (index < items.length - 1) {
        nextItem = items[index + 1];
      }

      if (nextItem) {
        nextEntry = checklistTemplateEntries.get(nextItem.id);
      }

      if (entry.groupedWithPrevious) {
        if (!currentGroup) {
          currentGroup = new WorkflowOverviewGroup();
          currentGroup.items = [];
        }
        currentGroup.items.push(item);
      } else if (nextEntry?.groupedWithPrevious) {
        if (currentGroup) {
          if (currentGroup.items.length === 1) {
            groupedItems.push(currentGroup.items[0]);
          } else {
            groupedItems.push(currentGroup);
          }
        }
        currentGroup = new WorkflowOverviewGroup();
        currentGroup.items.push(item);
      } else {
        if (currentGroup) {
          if (currentGroup.items.length === 1) {
            groupedItems.push(currentGroup.items[0]);
          } else {
            groupedItems.push(currentGroup);
          }
          currentGroup = null;
        }
        groupedItems.push(item);
      }

    }

    if (currentGroup != null) {
      if (currentGroup.items.length === 1) {
        groupedItems.push(currentGroup.items[0]);
      } else {
        groupedItems.push(currentGroup);
      }
    }

    return groupedItems;
  }
}
